import React, { memo } from 'react';
import { StyleSheet, View, Modal } from 'react-native';

import { LoadingAnimation, theme, useResponsive } from '@sg/garnish';

import { useTrackEventEffect } from '@order/Telemetry';

import {
  LocationPermissionPrimer,
  LocationSearch,
  PageWrapper,
  useLocationPermissionPrimer,
} from '../../components';
import { useReorderState } from '../ReorderingScreen';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationsScreen = memo(() => {
  const { currentBreakpoint } = useResponsive();
  const {
    reordering: isReordering,
    restaurantSlug: reorderRestaurantSlug,

    deliveryOrderDetail,
  } = useReorderState();

  const {
    isPrimerActive,
    isRequestingLocationPermission,
    isSkippingLocationPermission,
    isLocationPermissionsLoading,
    isLocationPermissionDenied,
    isLocationPermissionSkipped,
    isLocationPermissionAlreadyShown,
    onAllow,
    onSkip,
  } = useLocationPermissionPrimer({ variants: LOCATIONS_SCREEN_VARIANTS });

  const interactedLocationRestaurantSlug = isReordering ? reorderRestaurantSlug : undefined;
  const interactedLocationDeliveryAddressId = isReordering
    ? deliveryOrderDetail?.addressId
    : undefined;

  const shouldRequestLocationPermission = !(
    isLocationPermissionDenied ||
    isLocationPermissionSkipped ||
    isLocationPermissionAlreadyShown
  );

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventEffect({ name: 'location.view' });

  // ─────────────────────────────────────────────────────────────────────

  if (isLocationPermissionsLoading) {
    return <LoadingAnimation withNavbarOffset backgroundColor={theme.colors.APP_BACKGROUND} />;
  }

  if (isPrimerActive) {
    return (
      <Modal visible>
        <LocationPermissionPrimer
          isRequesting={isRequestingLocationPermission}
          isSkipping={isSkippingLocationPermission}
          onAllow={onAllow}
          onSkip={onSkip}
        />
      </Modal>
    );
  }

  if (currentBreakpoint.isXS) {
    return (
      <View style={styles.wrapper}>
        <LocationSearch
          interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
          interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
          shouldRequestLocationPermission={shouldRequestLocationPermission}
        />
      </View>
    );
  }

  return (
    <PageWrapper contentContainerStyle={styles.wrapper}>
      <LocationSearch
        interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
        interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
      />
    </PageWrapper>
  );
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    height: '100%',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const LOCATIONS_SCREEN_VARIANTS: LocationPrimerVariants = ['B', 'C'];

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationPrimerVariants = Parameters<typeof useLocationPermissionPrimer>[0]['variants'];
